import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  SignUpModalContextProvider,
  HIDE_TIME_DELAY_MODAL,
  useSignUpModalContext
} from '@strava/authentication-ui';
import { trackV2 } from 'utils/analytics';
import FooterUpsell from '../../../components/FooterUpsell';
import { ActivityPublicContextProvider } from './ActivityPublicContext';
import MapElevationChartWrapper from '../MapElevationChartWrapper';
import AthleteBanner from './components/AthleteBanner';
import Comments from './components/Comments';
import Summary from './components/Summary';
import MoreActivities from './components/MoreActivities';
import Photos from './components/Photos';
import Upsell from './components/Upsell';
import SignUpModalWrapper from './components/SignUpModalWrapper';
import { isDev } from '../../../utils/devUtils';
import StravaPackageContextProviders from '../../StravaPackageContextProviders';
import styles from './styles.scss';

const okToOpenModal = () =>
  sessionStorage.getItem(HIDE_TIME_DELAY_MODAL) !== 'true';

const ActivityPublic = ({
  activity,
  photos,
  analyticsData,
  signUpModalProps
}) => {
  const { streams } = activity;

  const track = useCallback(
    (fields) => {
      trackV2({ ...analyticsData, ...fields });
    },
    [analyticsData]
  );

  const { openSignUpModal } = useSignUpModalContext();

  useEffect(() => {
    let timeout;
    const queryParams = window.location.search;
    const params = new URLSearchParams(queryParams);

    // only set timeout if modal has not already been seen
    if (okToOpenModal()) {
      // debugging & QA-tests only - ?fast=true in url will speed up signup modal delay timer
      const signupModalDelay = params.get('fast') === 'true' ? 2000 : 6000;

      timeout = setTimeout(() => {
        // check again that modal is ok to open after time delay
        if (okToOpenModal()) {
          openSignUpModal('generic_time_delayed');
        }
      }, signupModalDelay);
    }

    // debugging only - ?reset=true in url will remove HIDE_TIME_DELAY_MODAL from session storage
    if (params.get('reset') === 'true') {
      sessionStorage?.removeItem(HIDE_TIME_DELAY_MODAL);
    }

    return () => clearTimeout(timeout);
  }, [openSignUpModal]);

  return (
    <ActivityPublicContextProvider
      track={track}
      openSignUpModal={openSignUpModal}
      registerUrl={activity.registerUrl}
    >
      <main role="main">
        <AthleteBanner {...activity.athlete} />
        <div className="container">
          <div className={`row ${styles.row}`}>
            <div className={`col-md-6 ${styles.mapData}`}>
              {activity.map.present && streams && (
                <MapElevationChartWrapper streams={streams} />
              )}
            </div>
            <div className={`col-md-6 ${styles.activityData}`}>
              <Summary {...activity} />
              <Photos photos={photos} />
              <Comments
                comments={activity.comments}
                totalCommentCount={activity.commentCount}
              />
              <Upsell type={activity.type} />
            </div>
          </div>
        </div>

        <MoreActivities
          activities={activity.relatedActivities}
          athlete={activity.athlete}
        />
        <FooterUpsell
          authUrls={activity.authUrls}
          analyticsData={analyticsData}
          deeplink={activity.deeplink}
        />

        <SignUpModalWrapper category="activities" {...signUpModalProps} />
      </main>
    </ActivityPublicContextProvider>
  );
};

ActivityPublic.defaultProps = {
  photos: []
};

ActivityPublic.propTypes = {
  activity: PropTypes.shape({
    type: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    streams: PropTypes.object.isRequired,
    commentCount: PropTypes.number.isRequired,
    authUrls: PropTypes.object.isRequired,
    map: PropTypes.shape({
      present: PropTypes.any.isRequired,
      src: PropTypes.object.isRequired
    }),
    athlete: PropTypes.shape({}).isRequired,
    relatedActivities: PropTypes.array.isRequired,
    comments: PropTypes.array.isRequired,
    registerUrl: PropTypes.string.isRequired,
    deeplink: PropTypes.string.isRequired
  }).isRequired,
  photos: PropTypes.arrayOf(PropTypes.object),
  analyticsData: PropTypes.shape({
    category: PropTypes.string.isRequired,
    page: PropTypes.string.isRequired
  }).isRequired,
  signUpModalProps: PropTypes.shape({
    url: PropTypes.string,
    useMfe: PropTypes.bool
  }).isRequired
};

const ActivityPublicWrapper = ({ useMockData, ...props }) => {
  const [activityPublicProps, setActivityPublicProps] = useState(props);

  // because of the limits of developing locally with activities, can use param `use_mock_data=true`
  // to see a nicely fleshed-out logged-out activity detail page
  useEffect(() => {
    if (useMockData && isDev()) {
      import('./mockData.json')
        .then((module) => {
          const { signUpModalProps } = props;
          setActivityPublicProps({ ...module.default, signUpModalProps });
        })
        .catch((err) => {
          // eslint-disable-next-line no-console
          console.error(err);
        });
    }
    // something about this is causing an infinite loop, so this ensures that we only import the mock data once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <StravaPackageContextProviders>
      <SignUpModalContextProvider
        nameForFollowAthleteVariant={props.activity.athlete.firstName}
        athleteAvatar={props.activity.athlete.avatarUrl}
        defaultRedirectUrl={props.activity.activityPageUrl}
        redirectUrls={{'follow_athlete': props.activity.athlete.athletePageUrl}}
      >
        <ActivityPublic {...activityPublicProps} />
      </SignUpModalContextProvider>
    </StravaPackageContextProviders>
  );
};

ActivityPublicWrapper.propTypes = {
  useMockData: PropTypes.bool.isRequired,
  signUpModalProps: PropTypes.shape({
    useMfe: PropTypes.bool.isRequired
  }).isRequired,
  activity: PropTypes.shape({
    athlete: PropTypes.shape({
      firstName: PropTypes.string.isRequired,
      avatarUrl: PropTypes.string.isRequired,
      athletePageUrl: PropTypes.string.isRequired
    }).isRequired,
    activityPageUrl: PropTypes.string.isRequired
  }).isRequired
};

export default ActivityPublicWrapper;
